/**
 * The browser reads a listening document when no audio was generated (D-106): one line after
 * the other, each character with its own French (or English) voice chosen in @agencyos/core,
 * a short pause between two lines. Never the labels: the lines come already split.
 */

import { type DeviceVoice, type SpokenLine, planVoices } from "@agencyos/core";

import { deviceVoices, speechAvailable, stopSpeaking, utter } from "./audio";

export type SpeakResult = "done" | "blocked" | "stopped";

export interface SpeakOptions {
  /** « fr » or « en ». */
  lang: string;
  /** The voice language tried first (« fr-FR », « en-GB »). */
  preferred: string;
  /** Speed chosen in the review (0.75, 1, 1.25). */
  speed?: number;
  /** First line to read (review: « back » to the previous line). */
  from?: number;
  onLine?: (index: number) => void;
  pauseMs?: number;
}

let run = 0; // a new reading (or stop) ends the current one

export function stopLines(): void {
  run += 1;
  stopSpeaking();
}

/** Read the lines once; « blocked » when the browser refused to speak (no user gesture). Each
 * line goes through `utter` (short pieces, a watchdog): a long line never hangs (D-114). */
export async function speakLines(lines: readonly SpokenLine[], options: SpeakOptions): Promise<SpeakResult> {
  if (!speechAvailable() || !lines.length) return "done";
  stopLines();
  const mine = run;
  const found = await deviceVoices();
  const devices: DeviceVoice[] = found.map((v) => ({ id: v.voiceURI || v.name, name: v.name, lang: v.lang }));
  const plan = planVoices(lines, devices, options.lang, options.preferred);
  const speed = options.speed ?? 1;
  for (let n = options.from ?? 0; n < lines.length; n += 1) {
    if (mine !== run) return "stopped";
    const line = lines[n];
    if (!line) continue;
    options.onLine?.(n);
    const choice = plan.get(line.index);
    const voice = found.find((v) => (v.voiceURI || v.name) === choice?.voiceId);
    const outcome = await utter(line.text, {
      lang: options.preferred,
      voice,
      pitch: choice?.pitch ?? 1,
      rate: (choice?.rate ?? 0.95) * speed,
    });
    if (outcome === "blocked") return n === (options.from ?? 0) ? "blocked" : "stopped";
    if (outcome === "stopped" || mine !== run) return "stopped";
    if (n < lines.length - 1) await new Promise((resolve) => window.setTimeout(resolve, (options.pauseMs ?? 450) / speed));
  }
  return mine === run ? "done" : "stopped";
}
